import { ArenaState } from '@shared/types.js';
import type { ClientMessage, ServerMessage, ArenaPlayer, ArenaSpectator, ScoreEntry, StartPlayer, TickPlayer, Trail, Crash, Grid, Speed, Role, UnlockId } from '@shared/types.js';

export interface NetworkCallbacks {
  onWelcome(msg: { roomState: ArenaState }): void;
  onJoined(playerId: number): void;
  onReconnected(playerId: number, role: Role): void;
  onArena(msg: { players: ArenaPlayer[]; spectators: ArenaSpectator[]; hostId: number | null; speed: Speed; autoStart: boolean; autoStartIn: number | null; scores: Record<string, ScoreEntry>; unlock: UnlockId | null; afkThreshold: number; autoStartSeconds: number }): void;
  onYoureIn(msg: { playerId: number }): void;
  onAutoStart(msg: { seconds: number }): void;
  onGameStart(msg: { cols: number; rows: number; players: StartPlayer[]; tickMs: number; scores: Record<string, ScoreEntry> }): void;
  onSync(msg: { cols: number; rows: number; players: StartPlayer[]; grid: Grid; crashes: Crash[]; crashTick: number; scores: Record<string, ScoreEntry> }): void;
  onTick(msg: { players: TickPlayer[]; newTrails: Trail[]; newCrashes: Crash[]; crashTick: number; jumping: number[] }): void;
  onGameOver(msg: { msg: string; color: string; winnerId: number | null }): void;
  onStatus(msg: { online: number; gameInProgress: boolean }): void;
  onError(msg: string): void;
  onDisconnect(): void;
  onReconnecting(): void;
  // Reconnect was rejected because the session is unknown (e.g. the server
  // restarted and lost in-memory sessions). The socket is open and fresh, so
  // the client can recover by rejoining rather than hanging on RECONNECTING.
  onReconnectFailed(): void;
}

let ws: WebSocket | null = null;
let callbacks: NetworkCallbacks | null = null;
let statusWs: WebSocket | null = null;
let statusCallback: ((online: number, gameInProgress: boolean) => void) | null = null;

// Reconnection state
let reconnectToken: string | null = null;
let reconnectAttempt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let intentionalClose = false;
// True between sending a 'reconnect' and getting 'reconnected'/'error'. Covers
// the page-refresh case (initial token reconnect, reconnectAttempt still 0).
let reconnectInFlight = false;

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_MS = 1000;

const SESSION_TOKEN_KEY = 'tron_session_token';

function wsUrl(): string {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}`;
}

export function connectStatus(cb: (online: number, gameInProgress: boolean) => void): void {
  statusCallback = cb;
  if (statusWs) return;
  statusWs = new WebSocket(wsUrl());
  statusWs.onmessage = (e: MessageEvent) => {
    try {
      const msg = JSON.parse(e.data) as ServerMessage;
      if (msg.type === 'status') statusCallback?.(msg.online, msg.gameInProgress);
    } catch { /* ignore malformed messages */ }
  };
  statusWs.onclose = () => { statusWs = null; };
}

export function disconnectStatus(): void {
  if (statusWs) { statusWs.close(); statusWs = null; }
  statusCallback = null;
}

function setupWsHandlers(): void {
  if (!ws) return;

  ws.onmessage = (e: MessageEvent) => {
    let msg: ServerMessage;
    try { msg = JSON.parse(e.data); } catch { return; }
    switch (msg.type) {
      case 'welcome':
        if (reconnectToken) {
          // We have a token — attempt reconnect
          reconnectInFlight = true;
          send({ type: 'reconnect', token: reconnectToken });
        } else {
          callbacks!.onWelcome(msg);
        }
        break;
      case 'joined':
        reconnectToken = msg.token;
        sessionStorage.setItem(SESSION_TOKEN_KEY, msg.token);
        reconnectAttempt = 0;
        reconnectInFlight = false;
        callbacks!.onJoined(msg.playerId);
        break;
      case 'reconnected':
        reconnectAttempt = 0;
        reconnectInFlight = false;
        callbacks!.onReconnected(msg.playerId, msg.role);
        break;
      case 'arena':
        callbacks!.onArena(msg);
        break;
      case 'youreIn':
        callbacks!.onYoureIn(msg);
        break;
      case 'autoStart':
        callbacks!.onAutoStart(msg);
        break;
      case 'start':
        callbacks!.onGameStart(msg);
        break;
      case 'sync':
        callbacks!.onSync(msg);
        break;
      case 'tick':
        callbacks!.onTick(msg);
        break;
      case 'gameover':
        callbacks!.onGameOver(msg);
        break;
      case 'status':
        callbacks!.onStatus(msg);
        break;
      case 'error':
        // An error while a reconnect is in flight means the server no longer
        // knows the session (restart, or grace expired after a refresh). Clear
        // the stale token and let the client rejoin on the still-open socket
        // instead of hanging or stranding a ghost slot.
        if (reconnectInFlight) {
          reconnectInFlight = false;
          clearReconnectState();
          callbacks!.onReconnectFailed();
        } else {
          callbacks!.onError(msg.msg);
        }
        break;
    }
  };

  ws.onclose = () => {
    ws = null;
    if (intentionalClose) return;
    if (reconnectToken && reconnectAttempt < MAX_RECONNECT_ATTEMPTS) {
      callbacks?.onDisconnect();
      scheduleReconnect();
    } else {
      clearReconnectState();
      callbacks?.onError('Disconnected from server.');
    }
  };
}

function scheduleReconnect(): void {
  const delay = RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt);
  reconnectAttempt++;
  callbacks?.onReconnecting();
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    ws = new WebSocket(wsUrl());
    setupWsHandlers();
  }, delay);
}

/** Whether a prior session token is stored (e.g. after a page refresh). */
export function hasStoredSession(): boolean {
  return sessionStorage.getItem(SESSION_TOKEN_KEY) !== null;
}

function clearReconnectState(): void {
  reconnectToken = null;
  reconnectAttempt = 0;
  reconnectInFlight = false;
  sessionStorage.removeItem(SESSION_TOKEN_KEY);
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

export function connect(cbs: NetworkCallbacks): void {
  callbacks = cbs;
  intentionalClose = false;
  reconnectAttempt = 0;

  // Check for existing session token
  reconnectToken = sessionStorage.getItem(SESSION_TOKEN_KEY);

  ws = new WebSocket(wsUrl());
  setupWsHandlers();
}

export function send(msg: ClientMessage): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(msg));
  }
}

export function disconnect(): void {
  intentionalClose = true;
  clearReconnectState();
  if (ws) { ws.close(); ws = null; }
}
